import React from 'react';

import { Box, Typography } from '@mui/material';
import { FormikProps } from 'formik';

import {
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypes,
  currency,
  decimalMaxLength,
} from '@pharmaplan/common';
import { dropdownMapper } from '@pharmaplan/common/helpers/utils';

import useAllowanceWorkshiftDropdowns from '../../../../hooks/workshift/useAllowanceWorkshiftDropdowns';
import CustomTextField from '../../../common/CustomTextField/CustomTextField';
import CustomDropdown from '../../../common/CustomDropdown/CustomDropdown';
import IDropdownObject from '../../../../types/ICustomDropdown';
import { InputTypes } from '../../../../helpers/Constants';
import CustomSwitch from '../../../common/CustomSwitch';
import strings from '../../../../localization';

import AllowanceSubComponent from './AllowanceSubComponent';
import { allowanceConfig } from './helper';
import classes from './style';

const { travelAllowanceType, accommodationAllowanceType, mealAllowance } = AllowancesNames;
interface IAllowanceWorkshift {
  formik: FormikProps<any>;
  isCounterOffer?: boolean;
  isAdmin?: boolean;
  pharmacyId?: string;
  renderHourlyRate?: React.ReactNode;
  useApi?: boolean;
  showHourlyRate?: boolean;
}

const { paidHourlyRate, emergencyFees, otherAllowance } = AllowancesNames;
const { meal, travel, accommodation, emergency, others } = AllowanceEnums;

const { hourlyRate, title } = classes;

const AllowanceWorkshift = ({
  formik,
  isCounterOffer,
  isAdmin,
  pharmacyId,
  renderHourlyRate,
  useApi = true,
  showHourlyRate = true,
}: IAllowanceWorkshift) => {
  const { travelDropdowns, accommodationDropdowns } = useAllowanceWorkshiftDropdowns({ useApi, pharmacyId });

  const secondaryInputConfig = {
    [meal]: (
      <CustomTextField
        usePreFilledColors={isCounterOffer}
        customClass={classes.horizontalAllowances}
        placeholder={' '}
        formik={formik}
        id={mealAllowance}
        name={mealAllowance}
        maxLength={decimalMaxLength}
        label={strings.amount}
        suffix={currency}
        horizontal
      />
    ),
    [travel]: (
      <>
        <CustomDropdown
          name={travelAllowanceType}
          formik={formik}
          multiple
          menuItems={
            dropdownMapper(
              travelDropdowns ?? [],
              'key',
              'value',
            ) as IDropdownObject[]
          }
        />
        {formik.values[travelAllowanceType]?.map?.((item: number) =>
          (
            <AllowanceSubComponent
              key={`${item}--`}
              type={AllowanceTypes.travel}
              subType={item}
              formik={formik}
            />
          ))}
      </>
    ),
    [accommodation]: (
      <>
        <CustomDropdown
          name={accommodationAllowanceType}
          formik={formik}
          menuItems={
            dropdownMapper(
              accommodationDropdowns ?? [],
              'key',
              'value',
            ) as IDropdownObject[]
          }
        />
        <AllowanceSubComponent
          type={AllowanceTypes.accommodation}
          subType={formik.values[accommodationAllowanceType]}
          formik={formik}
        />
      </>
    ),
    [emergency]: (
      <CustomTextField
        usePreFilledColors={isCounterOffer}
        customClass={hourlyRate}
        placeholder={' '}
        maxLength={decimalMaxLength}
        formik={formik}
        suffix={currency}
        id={emergencyFees}
        name={emergencyFees}
        horizontal
        label={strings.amount}
      />
    ),
    [others]: (
      <CustomTextField
        usePreFilledColors={isCounterOffer}
        customClass={hourlyRate}
        placeholder={' '}
        maxLength={decimalMaxLength}
        formik={formik}
        suffix={currency}
        id={otherAllowance}
        name={otherAllowance}
        horizontal
        label={strings.amount}
      />
    ),
  };

  return (
    <Box>
      <Typography sx={title}>{strings.rateAndAllowances}</Typography>

      {!isAdmin && showHourlyRate && (
        <CustomTextField
          usePreFilledColors={isCounterOffer}
          label={strings.hourlyRate}
          name={paidHourlyRate}
          formik={formik}
          placeholder=" "
          customType={InputTypes.number}
          suffix="$"
          maxLength={decimalMaxLength}
          customClass={hourlyRate}
          id={paidHourlyRate}
          horizontal
        />
      )}

      {renderHourlyRate}

      <Box>
        {allowanceConfig().map((item) => {
          const { key, label } = item;
          const isSelected = formik.values?.[key];
          return (
            <Box sx={isSelected ? {} : classes.toggleContainer} key={key}>
              <CustomSwitch
                label={label}
                key={key}
                name={key}
                formik={formik}
              />
              {isSelected && secondaryInputConfig[key]}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AllowanceWorkshift;
